import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Checkbox,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
} from '@mui/material';
import {
  PhotoLibrary as PhotoLibraryIcon,
  Close as CloseIcon,
  Collections as CollectionsIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    maxWidth: '90vw',
    maxHeight: '90vh',
    width: '1200px',
  },
}));

const PhotoCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    transform: 'scale(1.02)',
    boxShadow: theme.shadows[8],
  },
  '& .photo-overlay': {
    position: 'absolute',
    top: 8,
    right: 8,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    borderRadius: '50%',
    padding: 4,
  },
}));

interface Photo {
  id: string;
  url: string;
  thumbnailUrl?: string;
  name: string;
  takenAt?: any;
  location?: {
    latitude: number;
    longitude: number;
    address?: string;
  };
  metadata?: {
    camera?: string;
    settings?: string;
  };
}

interface CreateAlbumFromPhotosProps {
  open: boolean;
  onClose: () => void;
  photos: Photo[];
  onCreateAlbum: (albumData: {
    title: string;
    description: string;
    photoIds: string[];
    type?: 'private' | 'public' | 'paid';
    enableParticipantParticipation?: boolean;
    albumDate?: string;
  }) => Promise<void>;
  tripId?: string;
  eventId?: string;
}

const CreateAlbumFromPhotos: React.FC<CreateAlbumFromPhotosProps> = ({
  open,
  onClose,
  photos,
  onCreateAlbum,
  tripId,
  eventId
}) => {
  const [albumTitle, setAlbumTitle] = useState('');
  const [albumDescription, setAlbumDescription] = useState('');
  const [albumDate, setAlbumDate] = useState('');
  const [selectedPhotos, setSelectedPhotos] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);


  const handlePhotoToggle = (photoId: string) => {
    const newSelected = new Set(selectedPhotos);
    if (newSelected.has(photoId)) {
      newSelected.delete(photoId);
    } else {
      newSelected.add(photoId);
    }
    setSelectedPhotos(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedPhotos.size === photos.length) {
      setSelectedPhotos(new Set());
    } else {
      setSelectedPhotos(new Set(photos.map(p => p.id)));
    }
  };

  const handleCreateAlbum = async () => {
    if (!albumTitle.trim()) return;
    
    setLoading(true);
    try {
      await onCreateAlbum({
        title: albumTitle,
        description: albumDescription,
        photoIds: Array.from(selectedPhotos),
        type: 'private',
        enableParticipantParticipation: true,
        albumDate: albumDate || undefined
      });
      
      // Reset form
      setAlbumTitle('');
      setAlbumDescription('');
      setAlbumDate('');
      setSelectedPhotos(new Set());
      
      onClose();
    } catch (error) {
      console.error('Error creating album:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setAlbumTitle('');
    setAlbumDescription('');
    setAlbumDate('');
    setSelectedPhotos(new Set());
    onClose();
  };

  return (
    <StyledDialog open={open} onClose={handleClose} maxWidth={false}>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <CollectionsIcon color="primary" />
            <Typography variant="h6">Create Photo Album</Typography>
          </Box>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Album Title"
                value={albumTitle}
                onChange={(e) => setAlbumTitle(e.target.value)}
                required
                placeholder="Enter album title..."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={albumDescription}
                onChange={(e) => setAlbumDescription(e.target.value)}
                placeholder="Describe your album..."
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Album Date"
                type="date"
                value={albumDate}
                onChange={(e) => setAlbumDate(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
          </Grid>
          
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Photo Selection */}
        <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h6">
            Select Photos (Optional - {selectedPhotos.size} of {photos.length} selected)
          </Typography>
          {photos.length > 0 && (
            <Button
              variant="outlined"
              onClick={handleSelectAll}
              startIcon={<PhotoLibraryIcon />}
            >
              {selectedPhotos.size === photos.length ? 'Deselect All' : 'Select All'}
            </Button>
          )}
        </Box>

        {photos.length === 0 ? (
          <Alert severity="info">
            No photos available. You can create an empty album and add photos later.
          </Alert>
        ) : (
          <Grid container spacing={2}>
            {photos.map((photo) => (
              <Grid item xs={6} sm={4} md={3} lg={2} key={photo.id}>
                <PhotoCard onClick={() => handlePhotoToggle(photo.id)}>
                  <CardMedia
                    component="img"
                    height="120"
                    image={photo.thumbnailUrl || photo.url}
                    alt={photo.name}
                    sx={{ objectFit: 'cover' }}
                  />
                  <Box className="photo-overlay">
                    <Checkbox
                      checked={selectedPhotos.has(photo.id)}
                      onChange={() => handlePhotoToggle(photo.id)}
                      size="small"
                      sx={{ color: 'white' }}
                    />
                  </Box>
                  <CardContent sx={{ p: 1 }}>
                    <Typography variant="caption" noWrap>
                      {photo.name}
                    </Typography>
                    {photo.takenAt && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {new Date(photo.takenAt.seconds * 1000 || photo.takenAt).toLocaleDateString()}
                      </Typography>
                    )}
                  </CardContent>
                </PhotoCard>
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleCreateAlbum}
          disabled={!albumTitle.trim() || loading}
          startIcon={loading ? <CircularProgress size={20} /> : <CollectionsIcon />}
        >
          {selectedPhotos.size > 0 
            ? `Create Album (${selectedPhotos.size} photos)` 
            : 'Create Album'}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default CreateAlbumFromPhotos; 